import EventEmitter from "eventemitter3";

import { type EnforceObjectType } from "./utils";

export interface IKeybind {
  key:
    | (KeyboardEvent["code"] & {})
    | "LCLICK"
    | "RCLICK"
    | "MCLICK"
    | "LONG_PRESS";

  alt?: boolean;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
}

interface EmittedKeybind {
  clientX: number;
  clientY: number;
}

export const enforceObjectType: EnforceObjectType<IKeybind[]> = (v) => v;

export const REACTION_KEYBINDS = [
  "REACTION_LOVE",
  "REACTION_JAM",
  "REACTION_CUTE",
  "REACTION_POG",
  "REACTION_YAY",
  "REACTION_BONGO",
  "REACTION_SHOCKED",
  "REACTION_WORK",
  "REACTION_CRY",
  "REACTION_AWE",
] as const;

const unmodifiedKeybind = (...keys: IKeybind["key"][]): IKeybind[] =>
  keys.map((key) => ({
    key,
    alt: false,
    ctrl: false,
    meta: false,
    shift: false,
  }));

const shiftKeybind = (...keys: IKeybind["key"][]): IKeybind[] =>
  keys.map((key) => ({
    key,
    alt: false,
    ctrl: false,
    meta: false,
    shift: true,
  }));

const ctrlKeybind = (...keys: IKeybind["key"][]): IKeybind[] =>
  keys.map((key) => ({
    key,
    alt: false,
    ctrl: true,
    meta: false,
    shift: false,
  }));

const altKeybind = (...keys: IKeybind["key"][]): IKeybind[] =>
  keys.map((key) => ({
    key,
    alt: true,
    ctrl: false,
    meta: false,
    shift: false,
  }));

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  Boolean(
    target.closest(
      'input, textarea, select, [contenteditable="true"], [role="textbox"]',
    ),
  );

const KEYBINDS = enforceObjectType({
  PAN_UP: unmodifiedKeybind("KeyW", "ArrowUp"),
  PAN_DOWN: unmodifiedKeybind("KeyS", "ArrowDown"),
  PAN_LEFT: unmodifiedKeybind("KeyA", "ArrowLeft"),
  PAN_RIGHT: unmodifiedKeybind("KeyD", "ArrowRight"),
  ZOOM_OUT: unmodifiedKeybind("KeyQ", "Minus"),
  ZOOM_IN: unmodifiedKeybind("KeyE", "Equal"),
  ZOOM_OUT_SMALL: shiftKeybind("KeyQ", "Minus"),
  ZOOM_IN_SMALL: shiftKeybind("KeyE", "Equal"),
  PIXEL_WHOIS: [
    ...shiftKeybind("LCLICK"),
    ...unmodifiedKeybind("LONG_PRESS"),
  ],
  TEMPLATE_MOVE: altKeybind("LCLICK"),
  TOGGLE_TEMPLATE: unmodifiedKeybind("KeyT"),
  TOGGLE_BLANK: unmodifiedKeybind("KeyV", "KeyB"),
  TOGGLE_HEATMAP: unmodifiedKeybind("KeyH"),
  TOGGLE_GRID: unmodifiedKeybind("KeyG"),
  TOGGLE_UI: unmodifiedKeybind("KeyU"),
  TOGGLE_SETTINGS: unmodifiedKeybind("KeyO"),
  TOGGLE_INFO: unmodifiedKeybind("KeyI"),
  TOGGLE_MOD_MENU: unmodifiedKeybind("KeyM"),
  DESELECT_COLOR: unmodifiedKeybind("Escape"),
  SWAP_COLORS: unmodifiedKeybind("KeyX"),
  PICK_COLOR: unmodifiedKeybind("KeyC"),
  PICK_CANVAS_COLOR: unmodifiedKeybind("MCLICK"),
  MOD_SELECT: ctrlKeybind("LCLICK"),
  CAPTURE_CANVAS: unmodifiedKeybind("KeyP"),
  CAPTURE_VIEW: unmodifiedKeybind("KeyL"),
  CAPTURE_REGION: shiftKeybind("KeyL"),
  CAPTURE_TEMPLATE: shiftKeybind("KeyP"),
  REACTION_LOVE: unmodifiedKeybind("Digit1", "Numpad1"),
  REACTION_JAM: unmodifiedKeybind("Digit2", "Numpad2"),
  REACTION_CUTE: unmodifiedKeybind("Digit3", "Numpad3"),
  REACTION_POG: unmodifiedKeybind("Digit4", "Numpad4"),
  REACTION_YAY: unmodifiedKeybind("Digit5", "Numpad5"),
  REACTION_BONGO: unmodifiedKeybind("Digit6", "Numpad6"),
  REACTION_SHOCKED: unmodifiedKeybind("Digit7", "Numpad7"),
  REACTION_WORK: unmodifiedKeybind("Digit8", "Numpad8"),
  REACTION_CRY: unmodifiedKeybind("Digit9", "Numpad9"),
  REACTION_AWE: unmodifiedKeybind("Digit0", "Numpad0"),
  KEYBINDS: shiftKeybind("Slash"),
});

class KeybindManager_ extends EventEmitter<{
  [k in keyof typeof KEYBINDS]: (args: EmittedKeybind) => void;
}> {
  private enabled = true;

  constructor() {
    super();
    // setup listeners

    document.addEventListener("keydown", this.handleKeydown, {
      passive: false,
    });
    document.addEventListener("keyup", this.handleKeyup);
    document.addEventListener("click", this.handleClick); // only gets triggered for left click
    document.addEventListener("auxclick", (e) => {
      if (e.button === 0) return; // left button still triggers this
      this.handleClick(e);
    });
  }

  destroy() {
    // remove listeners
    // this is global and doesn't depend on any elements, so this shouldn't need to be called
  }

  handleKeydown = (e: KeyboardEvent) => {
    if (!this.enabled) return;

    if (isTypingTarget(e.target)) return;

    if (e.key === "Alt") e.preventDefault();
    if (e.key === "Control") e.preventDefault();
    if (e.key === "Shift") e.preventDefault();
  };

  handleKeyup = (e: KeyboardEvent) => {
    if (!this.enabled) return;

    if (isTypingTarget(e.target)) return;

    const isHandled = this.handleInteraction(
      {
        key: e.code,
        alt: e.altKey,
        ctrl: e.ctrlKey,
        meta: e.metaKey,
        shift: e.shiftKey,
      },
      {
        clientX: -1,
        clientY: -1,
      },
    );

    if (isHandled) e.preventDefault();
  };

  handleClick = (e: MouseEvent) => {
    const button: "LCLICK" | "RCLICK" | "MCLICK" = [
      "LCLICK",
      "MCLICK",
      "RCLICK",
    ][e.button] as any;

    const isHandled = this.handleInteraction(
      {
        key: button,
        alt: e.altKey,
        ctrl: e.ctrlKey,
        meta: e.metaKey,
        shift: e.shiftKey,
      },
      {
        clientX: e.clientX,
        clientY: e.clientY,
      },
    );

    if (isHandled) e.preventDefault();
  };

  /**
   * Handle interaction
   * @param key
   * @returns if handled
   */
  handleInteraction(key: IKeybind, emit: EmittedKeybind): boolean {
    let isHandled = false;

    for (const [name_, keybinds] of Object.entries(KEYBINDS)) {
      const name: keyof typeof KEYBINDS = name_ as any;

      const valid = keybinds.find((kb) => {
        if (kb.key !== key.key) return false;
        if (typeof kb.alt !== "undefined" && kb.alt !== key.alt) return false;
        if (typeof kb.ctrl !== "undefined" && kb.ctrl !== key.ctrl)
          return false;
        if (typeof kb.meta !== "undefined" && kb.meta !== key.meta)
          return false;
        if (typeof kb.shift !== "undefined" && kb.shift !== key.shift)
          return false;

        return true;
      });

      if (!valid) continue;

      this.emit(name, emit);
      isHandled = true;
    }

    return isHandled;
  }

  getKeybind(key: keyof typeof KEYBINDS) {
    return KEYBINDS[key];
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  isEnabled() {
    return this.enabled;
  }

  getKeybinds() {
    return { ...KEYBINDS };
  }
}

export const KeybindManager = new KeybindManager_();
